import { createLogger } from "@/lib/logger";
import { type Room } from "livekit-client";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = createLogger("learning-mode-transition");
const LEARNING_MODE_RPC_SWITCH = "learning_mode.switch";

export type LearningModeTransitionState = "idle" | "switching" | "ready" | "error";

interface UseLearningModeTransitionOptions {
  room: Room | null;
  agentIdentity?: string;
  enabled: boolean;
  targetLanguage: string;
  onChanged: (mode: "conversation") => void;
  minimumSpinnerMs?: number;
  readyConfirmationMs?: number;
}

interface LearningModeRpcResponse {
  ok?: boolean;
  mode?: string;
  error?: string;
}

function createRequestPayload() {
  const requestId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `learning-mode-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return JSON.stringify({ requestId, mode: "conversation", sentAtMs: Date.now() });
}

function parseResponse(value: string): LearningModeRpcResponse {
  try {
    return JSON.parse(value) as LearningModeRpcResponse;
  } catch {
    return { ok: false, error: "invalid_response" };
  }
}

function wait(milliseconds: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
}

export function useLearningModeTransition({
  room,
  agentIdentity,
  enabled,
  targetLanguage,
  onChanged,
  minimumSpinnerMs = 1200,
  readyConfirmationMs = 450,
}: UseLearningModeTransitionOptions) {
  const posthog = usePostHog();
  const [state, setState] = useState<LearningModeTransitionState>("idle");
  const [error, setError] = useState<string | null>(null);
  const stateRef = useRef<LearningModeTransitionState>("idle");
  const attemptRef = useRef(0);

  const transition = useCallback((next: LearningModeTransitionState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  useEffect(() => {
    if (enabled) return;
    attemptRef.current += 1;
    setError(null);
    transition("idle");
  }, [enabled, transition]);

  useEffect(
    () => () => {
      attemptRef.current += 1;
    },
    []
  );

  const switchToConversation = useCallback(async () => {
    if (stateRef.current === "switching" || stateRef.current === "ready") return;
    if (!enabled || !room || !agentIdentity) {
      setError("Your tutor is still getting ready. Try again in a moment.");
      transition("error");
      return;
    }

    const attempt = ++attemptRef.current;
    const startedAt = Date.now();
    setError(null);
    transition("switching");
    posthog?.capture("learning_mode_transition_requested", {
      from_learning_mode: "guided",
      to_learning_mode: "conversation",
      target_language: targetLanguage,
    });

    try {
      const raw = await room.localParticipant.performRpc({
        destinationIdentity: agentIdentity,
        method: LEARNING_MODE_RPC_SWITCH,
        payload: createRequestPayload(),
        responseTimeout: 10_000,
      });
      if (attempt !== attemptRef.current) return;
      const response = parseResponse(raw);
      if (!response.ok || response.mode !== "conversation") {
        throw new Error(response.error || "switch_failed");
      }

      const remainingSpinnerMs = minimumSpinnerMs - (Date.now() - startedAt);
      if (remainingSpinnerMs > 0) await wait(remainingSpinnerMs);
      if (attempt !== attemptRef.current) return;

      transition("ready");
      if (readyConfirmationMs > 0) await wait(readyConfirmationMs);
      if (attempt !== attemptRef.current) return;

      onChanged("conversation");
      transition("idle");
      posthog?.capture("learning_mode_transition_completed", {
        from_learning_mode: "guided",
        to_learning_mode: "conversation",
        target_language: targetLanguage,
        transition_duration_ms: Date.now() - startedAt,
      });
    } catch (cause) {
      if (attempt !== attemptRef.current) return;
      logger.error("Failed to switch the live session to Conversation mode", {
        error: cause instanceof Error ? cause.message : String(cause),
        targetLanguage,
      });
      setError("We couldn't switch modes. Try again.");
      transition("error");
      posthog?.capture("learning_mode_transition_failed", {
        from_learning_mode: "guided",
        to_learning_mode: "conversation",
        target_language: targetLanguage,
        error: cause instanceof Error ? cause.message : String(cause),
      });
    }
  }, [
    agentIdentity,
    enabled,
    minimumSpinnerMs,
    onChanged,
    posthog,
    readyConfirmationMs,
    room,
    targetLanguage,
    transition,
  ]);

  return {
    state,
    error,
    switchToConversation,
  };
}
